import { Dialog } from 'quasar'

import { getI18n } from '@/plugins/i18n'
import { useAccountingStore } from '@/stores/accounting.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useNotify } from '@/composables/notify'
import { formatCurrencyValue, useCurrency } from '@/composables/currency'
import { currencyCode } from '@/composables/exchange-rate'

import PromptDialog from '@/components/shared/PromptDialog.vue'

const canClaimFor = (staffId) => {
  const webSiteStore = useWebSiteStore()
  return !!staffId && (staffId === webSiteStore.staffId || webSiteStore.canApproveExpenseClaims)
}

export const isClaimableExpense = (row) => {
  const { costReportRowTypeEnum } = useAccountingStore()
  return row.type === costReportRowTypeEnum.EXPENSE && !row.expense_claim && !!row.date && canClaimFor(row.staff)
}

export const expenseRowAmount = (row) => (Number(row.quantity) || 0) * (Number(row.cost) || 0)

export const expenseClaimReport = (claim) => ({
  id: claim.report,
  name: claim.number,
  subject: useAccountingStore().reportSubjectEnum.STAFF,
  output_config: claim.output_config,
  language: claim.language,
  print_template: claim.print_template,
  column_template: claim.column_template,
  expense_claim: claim.id,
})

export const formatExpenseMoney = (value, currencyRef = null) => {
  if (value == null) return ''
  const code = currencyCode(currencyRef)
  return code ? useCurrency().getCurrencyString(Number(value), code) : formatCurrencyValue(value)
}

export const sumExpenseAmounts = (rows) => rows.reduce((total, row) => total + (Number(row.amount_reference) || 0), 0)

export const groupExpensesByDate = (rows) => {
  const byDate = new Map()
  for (const row of [...rows].sort((a, b) => (b.date || '').localeCompare(a.date || '') || b.id - a.id)) {
    if (!byDate.has(row.date)) byDate.set(row.date, [])
    byDate.get(row.date).push(row)
  }
  return [...byDate.entries()].map(([date, items]) => ({ date, rows: items, total: sumExpenseAmounts(items) }))
}

export const expenseClaimLastStepDate = (claim) => claim.updated_at

export const myExpenseParams = () => {
  const webSiteStore = useWebSiteStore()
  return {
    type: useAccountingStore().costReportRowTypeEnum.EXPENSE,
    ...(webSiteStore.canApproveExpenseClaims ? {} : { staff: webSiteStore.staffId }),
  }
}

export const unclaimedExpenseParams = () => ({ ...myExpenseParams(), without_claim: true })

export const expenseClaimDeclarationAsync = async() => {
  const accountingStore = useAccountingStore()
  const { data } = await accountingStore.api.getDocumentTermTemplatesAsync({ term_type: accountingStore.documentTermTemplateTypeEnum.EXPENSE_DECLARATION })
  return data[0]?.description || getI18n().t('common.expenseClaimDeclarationFallback')
}

export const useExpenseClaimActions = () => {
  const i18n = getI18n()
  const accountingStore = useAccountingStore()
  const webSiteStore = useWebSiteStore()
  const { notifySuccess, notifyWarning } = useNotify()
  const { expenseClaimStatusEnum } = accountingStore

  const canCreate = (staffId = webSiteStore.staffId) => {
    if (!canClaimFor(staffId)) notifyWarning(i18n.t('error.validation.reportStaffNotLinked'))
    return canClaimFor(staffId)
  }
  const isOwn = (claim) => !!claim.staff && claim.staff === webSiteStore.staffId
  const canApprove = (claim) => webSiteStore.canApproveExpenseClaims && claim.status === expenseClaimStatusEnum.SUBMITTED
  const canRevertApproval = (claim) => webSiteStore.canApproveExpenseClaims && claim.status === expenseClaimStatusEnum.APPROVED
  const canPay = (claim) => webSiteStore.can(webSiteStore.permissionFlagEnum.REIMBURSEMENTS) && claim.status === expenseClaimStatusEnum.APPROVED
  const editableStatuses = [expenseClaimStatusEnum.DRAFT, expenseClaimStatusEnum.REJECTED]
  const canEdit = (claim) => editableStatuses.includes(claim.status) && canClaimFor(claim.staff)
  const canSubmit = canEdit
  const ownerDeletableStatuses = [...editableStatuses, expenseClaimStatusEnum.SUBMITTED]
  const canDelete = (claim) => (isOwn(claim) && ownerDeletableStatuses.includes(claim.status)) ||
    (webSiteStore.canApproveExpenseClaims && claim.status !== expenseClaimStatusEnum.PAID)

  const transitionAsync = async(claims, status, data = {}) => {
    const updated = await accountingStore.transitionExpenseClaimsAsync({ ids: claims.map(claim => claim.id), status, ...data })
    notifySuccess(i18n.t('info.updatedSuccessfully'))
    return updated
  }

  const approveAsync = (claims, exclusions = {}) => transitionAsync(claims, expenseClaimStatusEnum.APPROVED, { exclusions })
  const revertApprovalAsync = (claims) => transitionAsync(claims, expenseClaimStatusEnum.SUBMITTED)

  const promptAsync = (componentProps) => new Promise((resolve) => {
    Dialog.create({ component: PromptDialog, componentProps })
      .onOk(resolve)
      .onCancel(() => resolve(null))
  })

  const rejectAsync = async(claim) => {
    const reason = await promptAsync({
      title: i18n.t('common.expenseClaimReject'),
      message: i18n.t('common.expenseClaimRejectMessage'),
      icon: 'mdi-close-circle-outline',
      iconColor: 'warning',
      inputLabel: i18n.t('common.expenseClaimRejectionReason'),
      okLabel: i18n.t('common.reject'),
      okColor: 'warning',
    })
    return reason ? transitionAsync([claim], expenseClaimStatusEnum.REJECTED, { reason }) : null
  }

  const submitAsync = async(claims) => {
    const declarationText = await expenseClaimDeclarationAsync()
    const accepted = await promptAsync({
      title: i18n.t('common.submitForApproval'),
      icon: 'mdi-clipboard-check-outline',
      checkLabel: declarationText,
      okLabel: i18n.t('common.submitForApproval'),
      okColor: 'positive',
    })
    return accepted ? transitionAsync(claims, expenseClaimStatusEnum.SUBMITTED, { declaration_text: declarationText }) : null
  }

  const payAsync = async(claims) => {
    const confirmed = await promptAsync({
      title: i18n.t('common.expenseClaimMarkPaid'),
      message: i18n.t('common.expenseClaimMarkPaidMessage', { count: claims.length }, claims.length),
      icon: 'mdi-cash-check',
      iconColor: 'positive',
      okColor: 'positive',
    })
    return confirmed ? transitionAsync(claims, expenseClaimStatusEnum.PAID) : null
  }

  return { canCreate, isOwn, canApprove, canRevertApproval, canPay, canEdit, canSubmit, canDelete, approveAsync, revertApprovalAsync, rejectAsync, submitAsync, payAsync, promptAsync }
}
